import React, { useContext, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiComment } from '@wix/da-papi-types';
import { PapiCommentWithChildren } from '../../types';
import { getIndentClassName } from '../../styles';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import Comment, { Props as CommentProps } from '../../../Comment/presets/Comment';
import Flex from '@wix/da-ds/pkg/Flex';
import ViewReplies from '../ViewReplies';
import { EditingState, CommentableTypes } from '../../../Comment/redux/types';
import { CommentPublisherResult } from '../../../CommentEditor/redux/types';
import s from './RecursiveComment.scss';
import { BiData, ClickViewReplyToCommentBiEvent } from '@wix/da-bi/pkg/events';

export interface Props {
  streamId: string;
  commentableTypeId: CommentableTypes;
  commentableItemId: number;
  comment: PapiCommentWithChildren;
  index: number;
  maxDepth: number;
  canPostComment?: boolean;
  isReplyingToParent: boolean;
  commentClassName?: string;
  commentWrapperClassName?: string;
  viewRepliesClassName?: string;
  enableForumThreadRootlessThreadMode?: boolean;
  /** we will move the reply indent level by this much */
  replyIndentModifier?: number;
  commentComponent?: React.ComponentType<CommentProps>;
  setCommentRef: (commentId: number, element: HTMLDivElement | null) => void;
  setVerticalIndicatorRef: (commentId: number, element: HTMLDivElement | null) => void;
  onCommentMouseOver: (
    commentId: number,
    parentId: number | null | undefined,
    isReplyingToParent: boolean
  ) => void;
  onEditingStateChange: (commentId: number, state: EditingState) => void;
  onOpenCommentModal: (
    typeId: number,
    itemId: number,
    commentId: number,
    comment: PapiComment
  ) => void;
  onThreadUpdated: (result: CommentPublisherResult) => void;
}

const RecursiveComment: React.FC<React.PropsWithChildren<Props>> = ({
  comment,
  index,
  ...props
}) => {
  const {
    maxDepth,
    commentableItemId,
    commentableTypeId,
    canPostComment,
    isReplyingToParent,
    commentComponent: CommentComponent = Comment,
    commentClassName,
    commentWrapperClassName,
    viewRepliesClassName,
    enableForumThreadRootlessThreadMode = false,
    setCommentRef,
    onCommentMouseOver,
    setVerticalIndicatorRef,
    onEditingStateChange,
    onOpenCommentModal,
    onThreadUpdated,
  } = props;
  const { commentId, indent, isLeafComment, children, parentId, replies } = comment;

  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const logBiEvent = useBiLogger();

  const openReplies = useCallback(() => {
    logBiEvent(
      BiData<ClickViewReplyToCommentBiEvent>({
        evid: 185,
        itemid: commentableItemId,
        typeid: commentableTypeId,
        commentid: commentId,
        commenterid: comment.user.useridUuid,
      })
    );

    // If we already in iframe just redirect
    if (window.self !== window.top) {
      window.location.assign(
        Url.commentLink(commentableTypeId, commentableItemId, commentId, undefined, true)
      );
      return;
    }

    onOpenCommentModal(commentableTypeId, commentableItemId, commentId, comment);
  }, [commentableItemId, commentableTypeId, logBiEvent, onOpenCommentModal, comment, commentId]);
  let hasMoreReplies = isLeafComment && replies > 0 && indent >= maxDepth;
  const indentClassName = getIndentClassName(indent, isMobile);

  // Special handling just for forum threads, deals with backend removing the root comment
  let hideParentIndicatorsIfIndentBelow;
  if (enableForumThreadRootlessThreadMode) {
    // This hides the parent lines as the parent is gone
    hideParentIndicatorsIfIndentBelow = 1;
    // We add 1 to indent as parent is gone and all idents are now one off
    hasMoreReplies = isLeafComment && replies > 0 && indent + 1 >= maxDepth;
  }

  return (
    <>
      <div
        className={classnames(
          indentClassName,
          s['comment-wrapper'],
          parentId && !isReplyingToParent && index > 0 && s['comment-wrapper-child'],
          hasMoreReplies && s['has-more-replies'],
          indent < hideParentIndicatorsIfIndentBelow && s['parent-indicators-hidden'],
          commentWrapperClassName
        )}
        ref={element => setCommentRef(commentId, element)}
        data-indent={indent}
        onMouseOver={() => onCommentMouseOver(commentId, parentId, isReplyingToParent)}
      >
        {parentId && index > 0 && (
          <div
            className={s['vertical-indicator']}
            ref={element => setVerticalIndicatorRef(commentId, element)}
          />
        )}
        <CommentComponent
          key={`comment-${commentId}`}
          comment={comment}
          className={classnames(s['comment'], commentClassName)}
          onReplyPosted={onThreadUpdated}
          onCommentUpdated={onThreadUpdated}
          canPostComment={!!canPostComment}
          onEditingStateChange={state => onEditingStateChange(commentId, state)}
        />
      </div>
      {children &&
        children.map(child => (
          <RecursiveComment
            key={`comment-wrap-${child.commentId}`}
            comment={child}
            index={child.index}
            {...props}
          />
        ))}
      {hasMoreReplies && (
        <Flex
          className={classnames(
            s['view-replies-wrapper'],
            viewRepliesClassName,
            isMobile && indentClassName
          )}
          justifyContent="flex-end"
        >
          <ViewReplies onClick={openReplies}>{t('commentthread.control.viewReplies')}</ViewReplies>
        </Flex>
      )}
    </>
  );
};
RecursiveComment.displayName = 'RecursiveComment';

export default RecursiveComment;
